import React, { useContext, useState, useEffect, useCallback } from 'react';
import AOS from 'aos';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import {
  FaTools,
  FaCogs,
  FaCheckCircle,
  FaAward,
  FaTruck,
  FaWarehouse,
  FaShieldAlt,
  FaHeadset,
  FaPhoneAlt,
  FaWhatsapp,
  FaNetworkWired,
  FaGlobe,
  FaBarcode
} from 'react-icons/fa';
import {
  RiCustomerService2Fill,
  RiSettings4Fill,
  RiSpeedUpFill,
  RiShieldCheckFill
} from 'react-icons/ri';
import { store } from '../App';
import Footer from './Footer';
import { apiUrl } from './Constants';
import '../css/Services.css';

const LOCAL_PAGE_URL = 'http://localhost/foton-oman-backend/api/getServicePageDataBySlug/services';
const REMOTE_PAGE_URL = 'https://dev10.sbagh.com/api/getServicePageDataBySlug/service-and-parts';
const LOCAL_TAKE_CARE_BASE = 'http://localhost/foton-oman-backend/public/api/';

const FALLBACK_IMAGES = {
  'service:hero': '/Images/service/service_banner.jpg',
  'parts:hero': '/Images/service/parts_banner.jpg',
  'service:total_care': '/Images/service/service_total_care.jpg',
  'service:support': '/Images/service/service_remote_support.jpg',
  'parts:intro': '/Images/service/parts_genuine.jpg',
  'parts:logistics': '/Images/service/parts_centers.jpg',
  'parts:packaging': '/Images/service/parts_packaging_1.jpg',
};

const TRAINING_FALLBACKS = [
  '/Images/service/service_training_centers.jpg',
  '/Images/service/service_training_team.jpg',
];

const STAT_ICONS = [FaNetworkWired, FaTools, FaGlobe, FaTruck];
const PILLAR_ICONS = [FaAward, FaCogs, FaWarehouse, RiSpeedUpFill];
const QUALITY_ICONS = [FaAward, FaShieldAlt, RiShieldCheckFill];
const PACKAGING_ICONS = [FaBarcode, FaShieldAlt, FaWarehouse];
const HERO_ICONS = [FaGlobe, FaShieldAlt, FaHeadset];

const isLocalHost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const takeCareUrls = (tab) => {
  const remoteBase = apiUrl || 'https://dev10.sbagh.com/api/';
  const remote = `${remoteBase}getTakeCarePage/${tab}`;
  const local = `${LOCAL_TAKE_CARE_BASE}getTakeCarePage/${tab}`;
  return isLocalHost() ? [remote, local] : [remote];
};

const loadTakeCareTab = async (tab) => {
  for (const url of takeCareUrls(tab)) {
    try {
      const res = await axios.get(url);
      const ok = res?.data?.success === true;
      const sections = ok ? normalizeSections(res?.data?.data) : null;
      if (ok && sections) return { ok: true, sections };
    } catch (error) {
      // Try the next endpoint. The CMS image is published on image_url.
    }
  }
  return { ok: false, sections: [] };
};

const hasText = (value) => value != null && String(value).trim() !== '';

const pick = (entity, key, isAr) => {
  if (!entity) return '';
  const english = entity[`${key}_en`];
  const arabic = entity[`${key}_ar`];
  if (isAr && hasText(arabic)) return String(arabic);
  return hasText(english) ? String(english) : '';
};

const normalizeSections = (value) => {
  const list = Array.isArray(value)
    ? value
    : (value && Array.isArray(value.sections) ? value.sections : null);
  if (!list) return null;
  return list
    .slice()
    .sort((a, b) => (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0))
    .map((section) => ({
      ...section,
      items: (Array.isArray(section?.items) ? section.items : [])
        .slice()
        .sort((a, b) => (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0)),
    }));
};

const itemsOf = (section, type) =>
  (section?.items || []).filter((item) => item?.item_type === type);

const imageSrc = (url, fallback) => (hasText(url) ? url : (fallback || ''));

const onImageError = (event, fallback) => {
  if (!fallback || event.currentTarget.dataset.fallback === '1') return;
  event.currentTarget.dataset.fallback = '1';
  event.currentTarget.src = fallback;
};

const isWhatsAppLink = (link) => /wa\.me|whatsapp/i.test(link || '');
const isTelLink = (link) => /^tel:/i.test(link || '');

function Services() {
  const [lang] = useContext(store);
  const [activeTab, setActiveTab] = useState('service');
  const [seo, setSeo] = useState(null);
  const [tabs, setTabs] = useState({ service: undefined, parts: undefined });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace('#', '').toLowerCase();
    if (hash === 'parts' || hash === 'spare-parts') setActiveTab('parts');
    else if (hash === 'service' || hash === 'services') setActiveTab('service');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const primaryUrl = isLocalHost() ? LOCAL_PAGE_URL : REMOTE_PAGE_URL;
    const fallbackUrl = isLocalHost() ? REMOTE_PAGE_URL : LOCAL_PAGE_URL;

    const applyPage = (payload) => {
      if (!payload || cancelled) return;
      if (payload.seo) setSeo(payload.seo);
    };

    axios.get(primaryUrl)
      .then((res) => {
        if (res?.data?.data) applyPage(res.data.data);
        else throw new Error('empty');
      })
      .catch(() => axios.get(fallbackUrl).then((res) => {
        if (res?.data?.data) applyPage(res.data.data);
      }).catch(() => {}));

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (tabs[activeTab] !== undefined) return undefined;
    let cancelled = false;
    loadTakeCareTab(activeTab).then((result) => {
      if (cancelled) return;
      setTabs((prev) => (
        prev[activeTab] !== undefined ? prev : { ...prev, [activeTab]: result }
      ));
    });
    return () => { cancelled = true; };
  }, [activeTab, tabs]);

  useEffect(() => {
    if (typeof AOS !== 'undefined') AOS.refresh();
  }, [activeTab, tabs]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState(null, '', `#${tab}`);
    }
  };

  const entry = tabs[activeTab];
  const sections = entry?.sections || [];
  const hero = sections.find((section) => section.section_key === 'hero');
  const heroImage = imageSrc(hero?.image_url, FALLBACK_IMAGES[`${activeTab}:hero`]);
  const loading = !entry;
  const empty = !!entry && (!entry.ok || sections.length === 0);

  const t = useCallback((entity, key) => pick(entity, key, lang), [lang]);

  const seoTitle = seo?.meta_title || t(hero, 'title') || 'Foton';
  const seoDesc = seo?.meta_description || t(hero, 'description') || '';

  const renderShowcase = (section, { imageFirst, featureStyle, BadgeIcon = FaAward }) => {
    const bullets = itemsOf(section, 'bullet');
    const features = itemsOf(section, 'feature');
    const eyebrow = t(section, 'eyebrow');
    const badge = t(section, 'badge');
    const title = t(section, 'title');
    const description = t(section, 'description');
    const fallback = FALLBACK_IMAGES[`${activeTab}:${section.section_key}`];
    const src = imageSrc(section.image_url, fallback);
    const featureIcons = featureStyle === 'icon' ? PACKAGING_ICONS : null;

    const textCol = (
      <Col lg={7} className="showcase-text-col">
        {eyebrow ? <span className="showcase-badge">{eyebrow}</span> : null}
        {title ? <h2 className="showcase-title">{title}</h2> : null}
        {description ? <p className="showcase-desc">{description}</p> : null}
        {bullets.length > 0 && (
          <ul className="showcase-features-list">
            {bullets.map((item) => (
              <li className="showcase-feature-item" key={item.id}>
                <FaCheckCircle />
                <span>{t(item, 'title')}</span>
              </li>
            ))}
          </ul>
        )}
        {features.length > 0 && featureStyle === 'icon' && (
          <Row className="g-3 mt-2">
            {features.map((item, index) => {
              const Icon = featureIcons[index % featureIcons.length];
              return (
                <Col sm={6} key={item.id || index}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border">
                    <Icon className="fs-3 text-primary flex-shrink-0" />
                    <div>
                      <strong className="d-block text-dark">{t(item, 'title')}</strong>
                      {t(item, 'subtitle') ? <small className="text-muted">{t(item, 'subtitle')}</small> : null}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
        {features.length > 0 && featureStyle === 'box' && (
          <Row className="g-3 mt-1">
            {features.map((item, index) => (
              <Col sm={6} key={item.id || index}>
                <div className="p-3 bg-light rounded border">
                  <h6 className="fw-bold mb-1 text-primary">{t(item, 'title')}</h6>
                  {t(item, 'subtitle') ? <small className="text-muted">{t(item, 'subtitle')}</small> : null}
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Col>
    );

    const imageCol = (
      <Col lg={5} className="showcase-image-col">
        {src ? (
          <img
            src={src}
            alt={title}
            className="showcase-img"
            onError={(event) => onImageError(event, fallback)}
          />
        ) : null}
        {badge ? (
          <div className="showcase-floating-badge">
            <BadgeIcon />
            <span>{badge}</span>
          </div>
        ) : null}
      </Col>
    );

    return (
      <section className="service-showcase-section" key={section.id || section.section_key}>
        <Container>
          <div className="service-showcase-card" data-aos="fade-up">
            <Row className="g-0 align-items-stretch">
              {imageFirst ? imageCol : textCol}
              {imageFirst ? textCol : imageCol}
            </Row>
          </div>
        </Container>
      </section>
    );
  };

  const renderNetwork = (section) => {
    const stats = itemsOf(section, 'stat');
    return (
      <section className="service-network-section" key={section.id || section.section_key}>
        <Container>
          <div className="section-header-block" data-aos="fade-up">
            {t(section, 'eyebrow') ? <span className="section-eyebrow">{t(section, 'eyebrow')}</span> : null}
            {t(section, 'title') ? <h2 className="section-main-title">{t(section, 'title')}</h2> : null}
            {t(section, 'description') ? <p className="section-subtitle-text">{t(section, 'description')}</p> : null}
          </div>
          {stats.length > 0 && (
            <div className="stats-banner-wrapper" data-aos="fade-up">
              <Container>
                <Row className="g-4">
                  {stats.map((item, index) => {
                    const Icon = STAT_ICONS[index % STAT_ICONS.length];
                    return (
                      <Col lg={3} sm={6} key={item.id || index}>
                        <div className="stat-item-box">
                          <Icon className="stat-card-icon" />
                          <div className="stat-card-number">{t(item, 'value')}</div>
                          <div className="stat-card-label">{t(item, 'title')}</div>
                          {t(item, 'subtitle') ? <div className="stat-card-sub">{t(item, 'subtitle')}</div> : null}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Container>
            </div>
          )}
        </Container>
      </section>
    );
  };

  const renderTraining = (section) => {
    const cards = itemsOf(section, 'card');
    return (
      <section className="service-training-section" key={section.id || section.section_key}>
        <Container>
          <div className="section-header-block" data-aos="fade-up">
            {t(section, 'eyebrow') ? <span className="section-eyebrow">{t(section, 'eyebrow')}</span> : null}
            {t(section, 'title') ? <h2 className="section-main-title">{t(section, 'title')}</h2> : null}
            {t(section, 'description') ? <p className="section-subtitle-text">{t(section, 'description')}</p> : null}
          </div>
          <Row className="g-4 alternating-cards-row">
            {cards.map((item, index) => {
              const fallback = TRAINING_FALLBACKS[index] || TRAINING_FALLBACKS[0];
              const src = imageSrc(item.image_url, fallback);
              return (
                <Col lg={6} key={item.id || index} data-aos="fade-up" data-aos-delay={(index + 1) * 100}>
                  <div className="alt-feature-card">
                    {src ? (
                      <div className="alt-card-img-wrap">
                        <img
                          src={src}
                          alt={t(item, 'title')}
                          className="alt-card-img"
                          onError={(event) => onImageError(event, fallback)}
                        />
                      </div>
                    ) : null}
                    <div className="alt-card-body">
                      <h3 className="alt-card-title">{t(item, 'title')}</h3>
                      {t(item, 'description') ? <p className="alt-card-text">{t(item, 'description')}</p> : null}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>
    );
  };

  const renderPillars = (section) => {
    const cards = itemsOf(section, 'card');
    return (
      <section className="pillars-grid-section" key={section.id || section.section_key}>
        <Container>
          <div className="section-header-block" data-aos="fade-up">
            {t(section, 'eyebrow') ? <span className="section-eyebrow">{t(section, 'eyebrow')}</span> : null}
            {t(section, 'title') ? <h2 className="section-main-title">{t(section, 'title')}</h2> : null}
            {t(section, 'description') ? <p className="section-subtitle-text">{t(section, 'description')}</p> : null}
          </div>
          <Row className="g-4">
            {cards.map((item, index) => {
              const Icon = PILLAR_ICONS[index % PILLAR_ICONS.length];
              return (
                <Col lg={3} sm={6} key={item.id || index} data-aos="fade-up" data-aos-delay={(index + 1) * 100}>
                  <div className="pillar-card">
                    <div className="pillar-icon-box"><Icon /></div>
                    <h3 className="pillar-title">{t(item, 'title')}</h3>
                    {t(item, 'description') ? <p className="pillar-desc">{t(item, 'description')}</p> : null}
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>
    );
  };

  const renderQuality = (section) => {
    const cards = itemsOf(section, 'card');
    return (
      <section className="core-values-section" key={section.id || section.section_key}>
        <Container>
          <div className="section-header-block" data-aos="fade-up">
            {t(section, 'eyebrow') ? <span className="section-eyebrow">{t(section, 'eyebrow')}</span> : null}
            {t(section, 'title') ? <h2 className="section-main-title">{t(section, 'title')}</h2> : null}
            {t(section, 'description') ? <p className="section-subtitle-text">{t(section, 'description')}</p> : null}
          </div>
          <Row className="g-4">
            {cards.map((item, index) => {
              const Icon = QUALITY_ICONS[index % QUALITY_ICONS.length];
              return (
                <Col lg={4} md={6} key={item.id || index} data-aos="fade-up" data-aos-delay={(index + 1) * 100}>
                  <div className="core-value-card">
                    <div className="core-value-badge"><Icon /></div>
                    <h3 className="core-value-title">{t(item, 'title')}</h3>
                    {t(item, 'description') ? <p className="core-value-desc">{t(item, 'description')}</p> : null}
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>
    );
  };

  const renderCta = (section) => {
    const buttons = itemsOf(section, 'button');
    const badge = t(section, 'badge');
    return (
      <Container key={section.id || section.section_key}>
        <div className="services-cta-banner" data-aos="fade-up">
          <div className="cta-text-side">
            {badge ? <p className="mb-2 fw-bold">{badge}</p> : null}
            {t(section, 'title') ? <h3>{t(section, 'title')}</h3> : null}
            {t(section, 'description') ? <p>{t(section, 'description')}</p> : null}
          </div>
          <div className="cta-action-buttons">
            {buttons.map((item, index) => {
              const link = item.link || '#';
              const whatsapp = isWhatsAppLink(link);
              const tel = isTelLink(link);
              const openNewTab = !tel && (item.link_target === '_blank' || whatsapp);
              const Icon = whatsapp ? FaWhatsapp : FaPhoneAlt;
              return (
                <a
                  key={item.id || index}
                  href={link}
                  className={whatsapp ? 'btn-cta-whatsapp' : 'btn-cta-primary'}
                  target={openNewTab ? '_blank' : undefined}
                  rel={openNewTab ? 'noopener noreferrer' : undefined}
                >
                  <Icon />
                  <span>{t(item, 'title')}</span>
                </a>
              );
            })}
          </div>
        </div>
      </Container>
    );
  };

  const renderSection = (section) => {
    switch (section.section_key) {
      case 'total_care':
      case 'intro':
        return renderShowcase(section, {
          imageFirst: false,
          featureStyle: 'box',
          BadgeIcon: section.section_key === 'intro' ? FaWarehouse : FaAward,
        });
      case 'support':
        return (
          <div className="service-remote-section py-5" key={section.id || section.section_key}>
            {renderShowcase(section, { imageFirst: true, featureStyle: 'box', BadgeIcon: FaHeadset })}
          </div>
        );
      case 'logistics':
        return (
          <div className="service-supply-section py-5" key={section.id || section.section_key}>
            {renderShowcase(section, { imageFirst: true, featureStyle: 'box', BadgeIcon: FaTruck })}
          </div>
        );
      case 'packaging':
        return (
          <div className="service-packaging-section py-4" key={section.id || section.section_key}>
            {renderShowcase(section, { imageFirst: false, featureStyle: 'icon' })}
          </div>
        );
      case 'network':
        return renderNetwork(section);
      case 'training':
        return renderTraining(section);
      case 'pillars':
        return renderPillars(section);
      case 'quality':
        return renderQuality(section);
      case 'cta':
        return renderCta(section);
      default:
        return null;
    }
  };

  const heroBullets = itemsOf(hero, 'bullet');

  return (
    <div className={`services-page-wrapper ${lang ? 'rtl' : 'ltr'}`}>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDesc ? <meta name="description" content={seoDesc} /> : null}
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/services` : '/services'} />
      </Helmet>

      <section
        className="services-hero-banner"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="services-hero-overlay"></div>
        <Container>
          <Row>
            <Col lg={10} className="services-hero-content" data-aos="fade-up">
              {t(hero, 'eyebrow') ? <span className="hero-badge-tag">{t(hero, 'eyebrow')}</span> : null}
              {t(hero, 'title') ? <h1 className="services-hero-title">{t(hero, 'title')}</h1> : null}
              {t(hero, 'description') ? <p className="services-hero-subtitle">{t(hero, 'description')}</p> : null}
              {heroBullets.length > 0 && (
                <div className="hero-highlights-row">
                  {heroBullets.map((item, index) => {
                    const Icon = HERO_ICONS[index % HERO_ICONS.length];
                    return (
                      <div className="hero-highlight-chip" key={item.id || index}>
                        <Icon />
                        <span>{t(item, 'title')}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      <nav className="services-tab-nav-wrapper">
        <Container>
          <div className="services-tab-bar">
            <button
              type="button"
              className={`service-tab-btn ${activeTab === 'service' ? 'active' : ''}`}
              onClick={() => handleTabChange('service')}
            >
              <RiCustomerService2Fill />
              <span>{lang ? 'خدمة فوتون (SERVICE)' : 'FOTON SERVICE'}</span>
            </button>
            <button
              type="button"
              className={`service-tab-btn ${activeTab === 'parts' ? 'active' : ''}`}
              onClick={() => handleTabChange('parts')}
            >
              <RiSettings4Fill />
              <span>{lang ? 'قطع الغيار الأصلية (PARTS)' : 'FOTON GENUINE PARTS'}</span>
            </button>
          </div>
        </Container>
      </nav>

      <main className={activeTab === 'service' ? 'tab-content-service' : 'tab-content-parts'}>
        {loading && <p className="text-center py-5 mb-0">Loading...</p>}
        {empty && <p className="text-center py-5 mb-0">No content available.</p>}
        {!loading && !empty && sections.map((section) => renderSection(section))}
      </main>

      <Footer />
    </div>
  );
}

export default Services;
